// REACT
import { useMemo, useState } from "react";

// MATERIAL UI
import { Box, Container, Link, MenuItem, Select, Stack, Typography } from "@mui/material";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";
import Title from "src/components/common/Title";
import Layout from "src/components/core/Layout";
import BackLink from "src/components/tax-center/BackLink";
import TaxAccountGate from "src/components/tax-center/TaxAccountGate";
import TaxTransactionReviewTable from "src/components/tax-center/TaxTransactionReviewTable";

// REDUX
import api from "src/store/features/api";

// STYLES & UTILS
import { BORDER, NAVY, SALMON, TEXT_MUTED } from "src/components/tax-center/styles";
import {
  buildTaxTransactionsArg,
  downloadTransactionsCsv,
} from "src/components/tax-center/utils";

const CURRENT_TAX_YEAR = new Date().getUTCFullYear();
const TAX_YEARS = [CURRENT_TAX_YEAR, CURRENT_TAX_YEAR - 1];

const TaxCenterTransactions = () => {
  const [taxYear, setTaxYear] = useState(CURRENT_TAX_YEAR);
  const queryArg = useMemo(() => buildTaxTransactionsArg(taxYear), [taxYear]);
  const transactionsQuery = api.useGetTaxTransactionsQuery(queryArg, { refetchOnFocus: false });

  const transactions = transactionsQuery.data?.data ?? [];

  const handleExportCsv = () => {
    if (transactions.length === 0) return;
    downloadTransactionsCsv(transactions, taxYear);
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={2.75}>
          <BackLink to="/tax-center" />
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "flex-end" }}
            justifyContent="space-between"
            gap={3}>
            <Box>
              <Title
                title={{ partA: "Transaction", partB: "review" }}
                subtitle="Deposits need an acquisition date and cost before they can be reported."
              />
            </Box>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Select
                value={taxYear}
                onChange={(event) => setTaxYear(Number(event.target.value))}
                renderValue={(value) => (
                  <Typography component="span" sx={{ fontSize: 14, color: NAVY }}>
                    Tax year <b style={{ fontWeight: 500 }}>{value}</b>
                  </Typography>
                )}
                sx={{
                  height: 40,
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                }}>
                {TAX_YEARS.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
              <ContainedButton
                color="navy"
                width="130px"
                height="40px"
                disabled={transactionsQuery.isLoading || transactions.length === 0}
                onClick={handleExportCsv}>
                Export CSV
              </ContainedButton>
            </Stack>
          </Stack>

          <TaxAccountGate>
            <TaxTransactionReviewTable taxYear={taxYear} />

            <Typography sx={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.5 }}>
              Prefer to work offline?{" "}
              <Link
                component="button"
                type="button"
                onClick={handleExportCsv}
                sx={{
                  color: SALMON,
                  fontSize: 13,
                  textDecoration: "none",
                  verticalAlign: "baseline",
                  "&:hover": { color: NAVY },
                }}>
                Export these deposits as CSV
              </Link>{" "}
              and ask your broker to provide the cost basis for you.
            </Typography>
          </TaxAccountGate>
        </Stack>
      </Container>
    </Layout>
  );
};

export default TaxCenterTransactions;
